import { FC } from 'react';
import type { Specialists } from '../model';
import { BillingResponsibility } from '../model/BillingResponsibility';
import { RhcThanksPage } from '../pages/HNC/HncThanks/RhcThanksPage';
import { AmplarThanksPage } from '../pages/HNC/HncThanks/AmplarThanksPage';

export type PrivateHospitalId = 'amplar' | 'rhc';

export type PrivateBookingStepId =
  | 'psychologists'
  | 'patientDetails'
  | 'documents'
  | 'gpDetails'
  | 'refererDetails'
  | 'summary'
  | 'thanks';

export interface SpecialistOrderingRule {
  readonly swap?: {
    readonly sourceId: Specialists[number]['id'];
    readonly targetId: Specialists[number]['id'];
  };
}

export interface SpecialistStepConfig {
  readonly orderingRules?: readonly SpecialistOrderingRule[];
  readonly requireCentreSelection?: boolean;
  readonly listSubtitle?: string;
}

export interface PrivateHospitalHeaderConfig {
  readonly logoHref: string;
  readonly logoSrc: string;
  readonly contactHref: string;
}

export interface PrivateHospitalBrandingConfig {
  readonly pageTitle: string;
  readonly faviconPath: string;
}

export interface ClientDetailsConfig {
  readonly subTitle: string;
  readonly mobilePhoneOfNokRequired: boolean;
  readonly relationshipOfNokRequired: boolean;
  readonly mentalHealthQuestion: boolean;
  readonly showReasonForReferral: boolean;
  readonly consent: boolean;
  readonly clientIdLabel: string; // use mmhClientId in backend
  readonly showClientIdInSummary: boolean;
  readonly showMobile: boolean;
  readonly showEmail: boolean;
  readonly showNextOfKin: boolean;
  readonly showAboriginalQuestion: boolean;
}
export interface PrivateHospitalStepConfig {
  readonly id: PrivateBookingStepId;
  readonly slug: string;
  readonly label: string;
}

export interface GpDetailsConfig {
  readonly showFaxAndEmial: boolean;
  readonly showGpDetailsPage: boolean;
  readonly showReferrerDetailsPage: boolean;
}
export interface ReferralDetailsConfig {
  readonly primaryPresentingProblem: boolean;
  readonly currentMedication: boolean;
  readonly mmhcCentreDetails: boolean;
  readonly firstUploadLabel: string;
  readonly firstUploadRequired: boolean;
  readonly showFirstUploadNote: boolean;
}
export interface ReferrerDetailsConfig {
  readonly showPhn: boolean;
  readonly firstNameLabel: string;
  readonly lastNameLabel: string;
  readonly phoneLabel: string;
  readonly emailLabel: string;
  readonly title: string;
  readonly phnLabelInSummary: string;
}

export interface ThanksConfig {
  readonly thanksComponent: FC
}
export interface PrivateHospitalConfig {
  readonly id: PrivateHospitalId;
  readonly label: string;
  readonly baseRoute: string;
  readonly steps: readonly PrivateHospitalStepConfig[];
  readonly specialists: SpecialistStepConfig;
  readonly header: PrivateHospitalHeaderConfig;
  readonly branding: PrivateHospitalBrandingConfig;
  readonly clientDetails: ClientDetailsConfig;
  readonly gpDetails: GpDetailsConfig;
  readonly referralDetails: ReferralDetailsConfig;
  readonly referrerDetails: ReferrerDetailsConfig;
  readonly thanks: ThanksConfig;
}

const privateHospitalConfigs: Record<PrivateHospitalId, PrivateHospitalConfig> = {
  amplar: {
    id: 'amplar',
    label: 'Amplar Health',
    baseRoute: '/__hnc/form/dbbt-referral',
    steps: [
      { id: 'psychologists', slug: 'psychologists', label: 'Psychologists' },
      { id: 'patientDetails', slug: 'patient-details', label: 'Patient Details' },
      { id: 'documents', slug: 'documents', label: 'Documents' },
      { id: 'gpDetails', slug: 'gp-details', label: 'GP Details' },
      { id: 'refererDetails', slug: 'referer-details', label: 'Referrer Details' },
      { id: 'summary', slug: 'confirmation', label: 'Confirmation' },
      { id: 'thanks', slug: 'thanks', label: 'Thanks' },
    ],
    specialists: {
      orderingRules: [
        {
          swap: {
            sourceId: 'ab06f857-01a3-445d-9c58-b3803e7ea9e8',
            targetId: 'd4f60640-5756-4d3a-ad2b-617856ce9f75',
          },
        },
      ],
    },
    header: {
      logoHref: 'https://virtualpsychology.amplarhealth.com.au/',
      logoSrc: '/amplar-logo.svg',
      contactHref: 'https://virtualpsychology.amplarhealth.com.au/contact',
    },
    branding: {
      pageTitle: 'Amplar Health | Bookings',
      faviconPath: '/favicon.ico',
    },
    clientDetails: {
      subTitle: 'Thank you for choosing Amplar Health Virtual Psychology Clinic.',
      mobilePhoneOfNokRequired: false,
      relationshipOfNokRequired: false,
      mentalHealthQuestion: true,
      consent: true,
      clientIdLabel: 'MMH Client ID',
      showReasonForReferral: true,
      showClientIdInSummary: false,
      showMobile: true,
      showEmail: true,
      showNextOfKin: true,
      showAboriginalQuestion: true,
    },
    gpDetails: {
      showFaxAndEmial: true,
      showGpDetailsPage: true,
      showReferrerDetailsPage: true,
    },
    referralDetails: {
      primaryPresentingProblem: false,
      currentMedication: false,
      mmhcCentreDetails: false,
      firstUploadLabel: 'If you have a referral or health summary from your GP please upload it here:',
      firstUploadRequired: false,
      showFirstUploadNote: true,
    },
    referrerDetails: {
      showPhn: true,
      firstNameLabel: 'Referrer First Name',
      lastNameLabel: 'Referrer Last Name',
      phoneLabel: 'Referrer Contact Phone Number',
      emailLabel: 'Referrer Contact Email',
      title: 'Referrer Details',
      phnLabelInSummary: 'PHN',
    },
    thanks: {
      thanksComponent: AmplarThanksPage,
    },
  },
  rhc: {
    id: 'rhc',
    label: 'RHC',
    baseRoute: '/__rhc/form/dbbt-referral',
    steps: [
      { id: 'psychologists', slug: 'psychologists', label: 'Psychologists' },
      { id: 'patientDetails', slug: 'patient-details', label: 'Patient Details' },
      { id: 'documents', slug: 'documents', label: 'Documents' },
      { id: 'summary', slug: 'confirmation', label: 'Summary & Confirmation' },
      { id: 'thanks', slug: 'thanks', label: 'Thanks' },
    ],
    specialists: {
      requireCentreSelection: true,
      listSubtitle: 'Choose a psychiatrist who best suits your schedule. Our team offers telehealth video sessions anywhere in Australia.',
    },
    header: {
      logoHref: 'https://ruralhealthconnect.com.au',
      logoSrc: '/rural-health-connect-logo.webp',
      contactHref: 'https://ruralhealthconnect.com.au/contact/',
    },
    branding: {
      pageTitle: 'RHC | Bookings',
      faviconPath: '/rhc-favicon.ico',
    },
    clientDetails: {
      subTitle: 'Thank you for choosing Telecare Virtual Psychology Clinic.',
      mobilePhoneOfNokRequired: true,
      relationshipOfNokRequired: true,
      mentalHealthQuestion: false,
      consent: false,
      clientIdLabel: 'Case Number',
      showReasonForReferral: false,
      showClientIdInSummary: true,
      showMobile: false,
      showEmail: false,
      showNextOfKin: false,
      showAboriginalQuestion: false,
    },
    referralDetails: {
      primaryPresentingProblem: true,
      currentMedication: true,
      mmhcCentreDetails: true,
      firstUploadLabel: 'If you have an intake referral form please upload here (IAR-DST):',
      firstUploadRequired: true,
      showFirstUploadNote: false,
    },
    referrerDetails: {
      showPhn: false,
      firstNameLabel: 'Contact person First Name',
      lastNameLabel: 'Contact person Last Name',
      phoneLabel: 'Contact person Phone Number',
      emailLabel: 'Contact person Email',
      title: 'Client\'s Centre Contact Details',
      phnLabelInSummary: 'Medicare Mental health centre',
    },
    gpDetails: {
      showFaxAndEmial: false,
      showGpDetailsPage: false,
      showReferrerDetailsPage: false,
    },
    thanks: {
      thanksComponent: RhcThanksPage,
    },
  },
};

const ACTIVE_PRIVATE_HOSPITAL_ID = process.env.REACT_APP_PRIVATE_HOSPITAL_ID?.toLowerCase() as PrivateHospitalId;

export const ACTIVE_PRIVATE_HOSPITAL_CONFIG = privateHospitalConfigs[ACTIVE_PRIVATE_HOSPITAL_ID];

export const getPrivateHospitalConfig = (id: PrivateHospitalId): PrivateHospitalConfig => privateHospitalConfigs[id];

export const getPrivateHospitalStepRoute = (
  hospitalConfig: PrivateHospitalConfig,
  stepId: PrivateBookingStepId,
): string | undefined => {
  const step = hospitalConfig.steps.find(({ id }) => id === stepId);
  if (!step) {
    return undefined;
  }
  return `${hospitalConfig.baseRoute}/${step.slug}`;
};

export const getPrivateStepRoute = (stepId: PrivateBookingStepId): string | undefined => (
  getPrivateHospitalStepRoute(ACTIVE_PRIVATE_HOSPITAL_CONFIG, stepId)
);

export const getSpecialistStepConfig = (): SpecialistStepConfig | undefined => (
  ACTIVE_PRIVATE_HOSPITAL_CONFIG.specialists
);

export const getPrivateHeaderConfig = (): PrivateHospitalHeaderConfig | undefined => (
  ACTIVE_PRIVATE_HOSPITAL_CONFIG.header
);

export const getPrivateBrandingConfig = (): PrivateHospitalBrandingConfig => (
  ACTIVE_PRIVATE_HOSPITAL_CONFIG.branding
);

export const getPrivateClientDetailsConfig = (): ClientDetailsConfig => (
  ACTIVE_PRIVATE_HOSPITAL_CONFIG.clientDetails
);

export const getPrivateGpDetailsConfig = (): GpDetailsConfig => (
  ACTIVE_PRIVATE_HOSPITAL_CONFIG.gpDetails
);

export const getPrivateReferralDetailsConfig = (): ReferralDetailsConfig => (
  ACTIVE_PRIVATE_HOSPITAL_CONFIG.referralDetails
);

export const getPrivateReferrerDetailsConfig = (): ReferrerDetailsConfig => (
  ACTIVE_PRIVATE_HOSPITAL_CONFIG.referrerDetails
);

export const getPrivateThanksConfig = (): ThanksConfig => (
  ACTIVE_PRIVATE_HOSPITAL_CONFIG.thanks
);

/** Private workflow billing; defaults to FACILITY when env is unset. */
export const getWorkflowBillingResponsibility = (): BillingResponsibility => {
  const value = process.env.REACT_APP_WORKFLOW_BILLING_RESPONSIBILITY?.toUpperCase();
  const values = Object.values(BillingResponsibility) as string[];
  if (value && values.includes(value)) {
    return value as BillingResponsibility;
  }
  return BillingResponsibility.FACILITY;
};

/** Public/self-booking billing; defaults to PATIENT when env is unset. */
export const getPublicBillingResponsibility = (): BillingResponsibility => {
  const value = process.env.REACT_APP_PUBLIC_BILLING_RESPONSIBILITY?.toUpperCase();
  const values = Object.values(BillingResponsibility) as string[];
  if (value && values.includes(value)) {
    return value as BillingResponsibility;
  }
  return BillingResponsibility.PATIENT;
};
